<style scoped>
@import url("https://unpkg.com/maplibre-gl@5.0.1/dist/maplibre-gl.css");
.modal.show {
	background-color: rgba(0, 0, 0, 0.5);
}
.map-container {
	border-radius: 8px;
	overflow: hidden;
	height: 300px;
	margin-top: 16px;
}
.map-container :deep(.maplibregl-map) {
	width: 100%;
	height: 100%;
}
</style>

<template>
	<div
		class="modal fade"
		:class="{ 'show d-block': show }"
		tabindex="-1"
		v-show="show"
		@click.self="handleCancel"
	>
		<div class="modal-dialog modal-dialog-centered modal-lg">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bi bi-geo-alt me-2"></i>Edit Address
					</h5>
					<button
						type="button"
						class="btn-close"
						@click="handleCancel"
					></button>
				</div>
				<div class="modal-body">
					<AddressSuggestion
						v-model="editingAddress"
						placeholder="Start typing an address (min 3 characters)"
						@suggestion-selected="onSuggestionSelected"
					/>
					<div ref="mapContainer" class="map-container"></div>
				</div>
				<div class="modal-footer">
					<button
						type="button"
						class="btn btn-outline-secondary"
						@click="handleCancel"
					>
						Cancel
					</button>
					<ButtonLoading
						@click="handleSave"
						:disabled="!editingAddress.gid"
						:loading="saving"
						icon="bi-check-lg"
						text="Save Address"
						variant="primary"
					/>
				</div>
			</div>
		</div>
	</div>
	<div
		class="modal-backdrop fade show"
		v-show="show"
		@click="handleCancel"
	></div>
</template>

<script setup lang="ts">
import { onUnmounted, ref, shallowRef, watch } from "vue";
import maplibregl from "maplibre-gl";

import AddressSuggestion from "@/components/AddressSuggestion.vue";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { Address, type Geocode, type GeocodeSuggestion } from "@/type/api";

interface Emits {
	(e: "close"): void;
	(e: "save", address: Address): void;
}
interface Props {
	address: Address;
	show: boolean;
}

const emit = defineEmits<Emits>();
const props = defineProps<Props>();

const editingAddress = ref(new Address());
const map = shallowRef<maplibregl.Map | null>(null);
const mapContainer = ref<HTMLDivElement | null>(null);
const marker = ref<maplibregl.Marker | null>(null);
const saving = ref(false);

function initMap() {
	if (!mapContainer.value || map.value) return;

	const style = "https://tiles.stadiamaps.com/styles/alidade_smooth.json";
	const mapOptions: maplibregl.MapOptions = {
		container: mapContainer.value,
		style: style,
		center: [-105, 39],
		zoom: 4,
	};

	const mapInstance = new maplibregl.Map(mapOptions);

	mapInstance.addControl(new maplibregl.NavigationControl(), "top-left");
	map.value = mapInstance;
}

function placeMarker(lat: number, lng: number) {
	if (!map.value) return;

	if (marker.value) {
		marker.value.remove();
	}

	const el = document.createElement("div");
	el.style.backgroundColor = "#dc3545";
	el.style.width = "20px";
	el.style.height = "20px";
	el.style.borderRadius = "50%";
	el.style.border = "2px solid white";
	el.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";

	marker.value = new maplibregl.Marker({ element: el })
		.setLngLat([lng, lat])
		.addTo(map.value);

	map.value.easeTo({ center: [lng, lat], zoom: 16, duration: 1500 });
}

async function onSuggestionSelected(suggestion: GeocodeSuggestion) {
	try {
		const url = `/api/geocode/by-gid/${suggestion.gid}`;
		const response = await fetch(url);
		if (!response.ok) {
			console.error("Failed to fetch geocode details", response.statusText);
			return;
		}
		const data = (await response.json()) as Geocode;

		editingAddress.value = new Address(
			data.address.country,
			data.address.gid,
			data.address.locality,
			data.address.number,
			data.address.postal_code,
			data.address.raw,
			data.address.region,
			data.address.street,
			data.address.unit,
			data.address.location,
		);

		if (data.address.location) {
			placeMarker(
				data.address.location.latitude,
				data.address.location.longitude,
			);
		}
	} catch (e) {
		console.error("Failed to fetch geocode details", e);
	}
}

function handleCancel() {
	emit("close");
}

function handleSave() {
	saving.value = true;
	emit("save", editingAddress.value);
	saving.value = false;
}

function resetState() {
	editingAddress.value = props.address
		? new Address(
				props.address.country,
				"",
				props.address.locality,
				props.address.number,
				props.address.postal_code,
				props.address.raw,
				props.address.region,
				props.address.street,
				props.address.unit,
				props.address.location,
			)
		: new Address();

	if (map.value && props.address.location) {
		placeMarker(
			props.address.location.latitude,
			props.address.location.longitude,
		);
	}
}

watch(
	() => props.show,
	(newVal) => {
		if (newVal) {
			resetState();
			setTimeout(() => {
				if (!map.value) {
					initMap();
				}
			}, 100);
		} else {
			if (marker.value) {
				marker.value.remove();
				marker.value = null;
			}
		}
	},
);

onUnmounted(() => {
	if (marker.value) {
		marker.value.remove();
		marker.value = null;
	}
	if (map.value) {
		map.value.remove();
		map.value = null;
	}
});
</script>
